import { setState, getState, subscribe, type MaintenanceStatus } from './state';
import { clearFollowUp, maybeProbe } from './detector';
import { startPoller, stopPoller } from './poller';
import { clampMessage } from './constants';

export { getState, subscribe } from './state';
export type { MaintenanceStatus, MaintenanceState } from './state';
export { maybeProbe, runProbe } from './detector';

// `satisfies` (TS 4.9+) makes the array a compile error if MaintenanceStatus
// gains a new variant without being added here. Plain `readonly MaintenanceStatus[]`
// would type-check fine but silently accept the gap.
const VALID_STATUSES = [
  'ready',
  'maintenance',
  'connection',
  'offline',
] as const satisfies readonly MaintenanceStatus[];

/**
 * Type guard for MaintenanceStatus. Returns true if `s` is one of the valid
 * status literals. Replaces the previous `as MaintenanceStatus` cast at the
 * dev-gate callsite with a compile-checked narrowing.
 */
function isMaintenanceStatus(s: unknown): s is MaintenanceStatus {
  return typeof s === 'string' && (VALID_STATUSES as readonly string[]).includes(s);
}

let _initialized = false;
let _controller: AbortController | null = null;
let _unsubscribe: (() => void) | null = null;

/**
 * Initialize the maintenance system. Called once from src/index.tsx
 * during app bootstrap. Sets up the dev gate, the visibility re-probe,
 * the recovery-poller subscription, and the browser online listener.
 *
 * Idempotent at module level: a `_initialized` flag prevents repeat calls
 * from doubling up listeners. Dev HMR cleanup hook below releases the
 * listeners on hot dispose so re-execution starts fresh.
 */
export function initializeMaintenance(): void {
  if (_initialized) return;
  _initialized = true;
  _controller = new AbortController();
  const { signal } = _controller;

  // Auto-start/stop the recovery poller based on state. Subscribed FIRST,
  // before any setState call (including the dev gate below) can fire, so
  // the initial transition into 'maintenance' is observed by the poller
  // wiring. If we registered this AFTER the dev gate, a forced
  // VITE_DEV_MAINTENANCE_INITIAL=maintenance state would never trigger
  // startPoller and the recovery path would be dead during local QA -
  // only the manual Try Again button would work.
  const syncPollerToState = (): void => {
    if (getState().status === 'maintenance') {
      startPoller();
    } else {
      stopPoller();
    }
  };
  _unsubscribe = subscribe(syncPollerToState);
  // HMR re-eval safety: subscribe() does NOT fire on attach. After Vite
  // hot-replaces this module, state.ts keeps its module-level singleton
  // (it has no dispose handler), so `state` may already be 'maintenance'
  // when the new index module evaluates. The dev gate's
  // setState({ status: 'maintenance' }) below would then be a no-op
  // (setState short-circuits writes when status is unchanged; see
  // state.ts), notify never fires, and the poller stays unstarted.
  // Calling syncPollerToState once immediately syncs to whatever state.ts
  // currently holds, regardless of whether the dev gate is configured or
  // whether HMR is in play.
  // Production-safe: on cold boot state is 'ready', so this is just a
  // single stopPoller() call against an interval that doesn't exist
  // (idempotent, verified by `stopPoller is safe when no interval
  // running` test in poller.test.ts).
  syncPollerToState();

  // Dev-only gate: short-circuit in production builds so the env-var lookup
  // is tree-shaken out and a misconfigured VITE_DEV_MAINTENANCE_INITIAL on
  // production env vars cannot accidentally brick the app for all users.
  if (import.meta.env.DEV) {
    // Dev gate: VITE_DEV_MAINTENANCE_INITIAL=maintenance|connection|offline forces
    // the corresponding state on app start. Used during local development to
    // exercise the takeover without simulating real failures. Defaults to 'ready'.
    //
    // Optional VITE_DEV_MAINTENANCE_MESSAGE injects a custom message body when
    // forcing the maintenance state. Useful for exercising the message-rendering
    // path without flipping the live operator flag (which would affect everyone
    // else hitting the preview deploy). Other states ignore the message because
    // 'connection' / 'offline' / 'ready' have hard-coded copy.
    const devInitial = import.meta.env.VITE_DEV_MAINTENANCE_INITIAL;
    if (isMaintenanceStatus(devInitial)) {
      const devMessage =
        devInitial === 'maintenance' ? import.meta.env.VITE_DEV_MAINTENANCE_MESSAGE : undefined;
      setState(
        devMessage
          ? { status: devInitial, customMessage: clampMessage(devMessage) }
          : { status: devInitial },
      );
    }
  }

  // Re-probe when user returns to tab. Maintenance that started while they
  // were away is otherwise invisible until the next failed query.
  if (typeof document !== 'undefined') {
    document.addEventListener(
      'visibilitychange',
      () => {
        if (document.visibilityState === 'visible') {
          void maybeProbe('visibility');
        }
      },
      { signal },
    );
  }

  // Browser 'online' event: Connection and Offline states recover when
  // the browser reports network is back. Re-run the full probe to confirm
  // (the user could be on a captive portal that fakes "online"). The
  // 'manual' reason bypasses the detector's debounce window since the
  // browser is signaling intentional recovery.
  if (typeof window !== 'undefined') {
    window.addEventListener(
      'online',
      () => {
        const status = getState().status;
        if (status === 'connection' || status === 'offline') {
          void maybeProbe('manual');
        }
      },
      { signal },
    );
  }
}

// Dev HMR cleanup: when Vite hot-replaces this module, the previously
// attached document/window listeners, state subscription, AND the active
// poller interval would otherwise leak. AbortController + unsubscribe +
// stopPoller release each in turn so the next initializeMaintenance()
// call after re-eval starts with a clean slate.
//
// Order matters: stopPoller() runs BEFORE _unsubscribe() because the
// state subscription is what would normally call stopPoller on its own
// when state transitions away from 'maintenance'. Once the subscription
// is detached, no future state change can naturally clear the OLD
// module's setInterval, so we must stop it directly first. The same
// reasoning applies to clearFollowUp(): a pending damping follow-up
// timer would otherwise tick on the dead module.
if (import.meta.hot) {
  import.meta.hot.dispose(() => {
    stopPoller();
    clearFollowUp('hmr');
    _controller?.abort();
    _unsubscribe?.();
    _initialized = false;
    _controller = null;
    _unsubscribe = null;
  });
}
